// Energy Rate Types - Database-driven rate configuration system
// See: docs/plans/PLAN-electric-rates-management.md

/**
 * Energy rate profile stored in database
 * Rates can be assigned at any level of the hierarchy:
 * Unit (override) → Site → Group → Company → System Default
 */
export interface EnergyRate {
  id: string;
  name: string;
  description?: string;

  // Rate values ($/kWh)
  gridSellRate: number;       // Revenue from selling to grid
  gridImportRate: number;     // Cost to buy from grid
  dieselCostPerGallon: number;

  // Utility metadata
  utilityProvider?: string;   // 'SCE', 'LADWP', 'PG&E', etc. (legacy text field)
  utilityProviderId?: string; // FK to utility_providers table
  nemVersion?: string;        // 'NEM 2.0', 'NEM 3.0'
  countryCode?: string;       // 'US', 'UK', 'CA'
  stateProvince?: string;     // 'CA', 'GA', 'TX' / 'BC', 'QC' / 'London'

  // Status flags
  isSystemDefault: boolean;
  isLocked: boolean;

  // Audit timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Effective rate resolved for a specific unit
 * Includes rate source information for transparency
 */
export interface EffectiveRate extends EnergyRate {
  /** Where the rate was resolved from in the hierarchy */
  source: RateSource;
  /** Display name of the entity providing the rate */
  sourceName: string;
  /** ID of the entity providing the rate (if applicable) */
  sourceId?: string;
}

/**
 * Rate source levels in resolution hierarchy
 * 'unit_override' = explicit unit rate with rate_override=true
 * 'location' = auto-assigned based on unit location (postal code or state)
 */
export type RateSource = 'unit_override' | 'location' | 'unit' | 'site' | 'group' | 'company' | 'industry' | 'system' | 'fallback';

/**
 * Rate assignment to an entity (legacy interface for backwards compatibility)
 */
export interface RateAssignment {
  entityType: 'company' | 'group' | 'unit';
  entityId: string;
  rateId: string;
  /** Only applicable for units - when true, use unit's rate instead of inheriting */
  isOverride?: boolean;
}

/**
 * Time-bounded rate assignment stored in rate_assignments table
 * Supports effective date ranges and hierarchical override
 */
export interface RateAssignmentRecord {
  id: string;
  rateId: string;
  entityType: 'company' | 'group' | 'unit';
  entityId: string;
  /** Start date of rate assignment (inclusive) - ISO date string */
  effectiveFrom: string;
  /** End date of rate assignment (inclusive), null = ongoing */
  effectiveTo: string | null;
  /** For units: distinguishes explicit override from inherited */
  isOverride: boolean;
  /** Priority for conflict resolution (higher = wins) */
  priority: number;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
}

/**
 * Form data for creating/editing a rate assignment
 */
export interface RateAssignmentFormData {
  rateId: string;
  entityType: 'company' | 'group' | 'unit';
  entityId: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isOverride?: boolean;
  priority?: number;
}

/**
 * Bulk rate assignment request payload
 */
export interface BulkRateAssignmentRequest {
  entityType: 'company' | 'group' | 'unit';
  entityIds: string[];
  rateId: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/**
 * Bulk rate assignment response
 */
export interface BulkRateAssignmentResponse {
  created: number;
  skipped: number;
  errors: Array<{
    entityId: string;
    error: string;
  }>;
}

/**
 * Unified rate assignment with resolved entity names
 * Used by the "Assigned Rates" tab for displaying all assignments
 */
export interface UnifiedRateAssignment {
  id: string;
  rateId: string;
  entityType: 'company' | 'group' | 'site' | 'unit';
  entityId: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isOverride: boolean;
  priority: number;
  createdAt: string;
  createdBy: string | null;
  // Resolved from joins
  rateName: string;
  gridImportRate: number;
  gridSellRate: number;
  utilityProvider: string | null;
  nemVersion: string | null;
  stateProvince: string | null;
  entityName: string;
  parentCompanyId: string | null;
  parentCompanyName: string | null;
  // Computed status
  status: 'active' | 'future' | 'expired';
}

/**
 * Filters for unified assignment listing
 */
export interface UnifiedAssignmentFilters {
  entityType?: 'company' | 'group' | 'site' | 'unit';
  rateId?: string;
  status?: 'active' | 'future' | 'expired';
  companyId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'created_at' | 'effective_from' | 'entity_type';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated response for unified assignments
 */
export interface UnifiedAssignmentListResponse {
  data: UnifiedRateAssignment[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
  };
}

/**
 * Bulk update dates request
 */
export interface BulkUpdateDatesRequest {
  assignmentIds: string[];
  effectiveFrom?: string;
  effectiveTo?: string | null;
}

/**
 * Bulk reassign request
 */
export interface BulkReassignRequest {
  assignmentIds: string[];
  newRateId: string;
}

/**
 * Bulk delete request
 */
export interface BulkDeleteRequest {
  assignmentIds: string[];
}

/**
 * Bulk operation response
 */
export interface BulkOperationResponse {
  success: boolean;
  updated?: number;
  deleted?: number;
  reassigned?: number;
  requested: number;
  newRateName?: string;
}

/**
 * Rate assignment with related rate and entity details (for display)
 */
export interface RateAssignmentWithDetails extends RateAssignmentRecord {
  rate: EnergyRate;
  entityName: string;
}

/**
 * Rate change audit log entry
 */
export interface RateChangeLog {
  id: string;
  rateId: string;
  changedBy: string;
  changedAt: string;
  changeType: 'create' | 'update' | 'delete';
  oldValues?: Partial<EnergyRate>;
  newValues?: Partial<EnergyRate>;
}

/**
 * Form data for creating/editing a rate profile
 */
export interface RateFormData {
  name: string;
  description?: string;
  gridSellRate: number;
  gridImportRate: number;
  dieselCostPerGallon: number;
  utilityProvider?: string;      // Legacy text field
  utilityProviderId?: string;    // FK to utility_providers table
  nemVersion?: string;
  countryCode?: string;          // 'US', 'UK', 'CA'
  stateProvince?: string;
  isLocked?: boolean;
}

/**
 * API response for rate list with pagination
 */
export interface RateListResponse {
  rates: EnergyRate[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * API response from get_effective_rate_for_date RPC function
 * Returns full rate object + assignment metadata
 */
export interface EffectiveRateResponse {
  // Rate identity
  rateId: string | null;
  rateName: string;
  description?: string;

  // Rate values
  gridSellRate: number;
  gridImportRate: number;
  dieselCostPerGallon: number;

  // Utility metadata
  utilityProvider?: string;
  nemVersion?: string;
  stateProvince?: string;

  // Assignment source
  rateSource: RateSource;
  sourceName: string;

  // Time bounds (from assignment, null if legacy FK or system default)
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}

/**
 * Rate assignment counts for display in admin UI
 */
export interface RateAssignmentCounts {
  rateId: string;
  companyCount: number;
  groupCount: number;
  unitCount: number;
  totalCount: number;
}

/**
 * Filter options for rate list
 */
export interface RateFilters {
  stateProvince?: string;
  utilityProvider?: string;
  nemVersion?: string;
  search?: string;
}

// ============================================================================
// Auto-Rate by Location Types
// ============================================================================

/**
 * Entity type for auto-rate settings
 */
export type AutoRateEntityType = 'company' | 'group' | 'unit';

/**
 * Auto-rate setting for an entity with inheritance resolution
 */
export interface AutoRateSetting {
  /** The entity type */
  entityType: AutoRateEntityType;
  /** The entity ID */
  entityId: string;
  /** The explicit setting value (null = inherit from parent) */
  enabled: boolean | null;
  /** The resolved effective value after inheritance */
  resolvedValue: boolean;
  /** Where the resolved value came from */
  resolvedFrom: 'company' | 'group' | 'unit';
  /** For units: whether rate_override is enabled (blocks auto-rate) */
  rateOverride?: boolean;
}

/**
 * Location info for a unit
 */
export interface UnitLocationInfo {
  postalCode: string | null;
  stateProvince: string | null;
  city: string | null;
  country: string | null;
}

/**
 * Preview of auto-assigned rate for a unit
 */
export interface AutoRatePreview {
  /** The rate that would be assigned */
  rateId: string;
  /** Rate name */
  rateName: string;
  /** Provider name */
  providerName: string;
  /** How the match was made */
  matchType: 'postal_code' | 'state';
  /** The value that matched (zip code or state code) */
  matchValue: string;
}

/**
 * Response from auto-rate preview endpoint
 */
export interface AutoRatePreviewResponse {
  /** Unit ID */
  unitId: string;
  /** Current effective rate info */
  currentEffectiveRate: {
    rateId: string | null;
    rateName: string;
    rateSource: RateSource;
    autoAssigned: boolean;
    providerName: string | null;
  };
  /** Unit's location info */
  locationInfo: UnitLocationInfo;
  /** Preview of what rate would be auto-assigned (null if no match) */
  previewAutoRate: AutoRatePreview | null;
  /** Whether enabling auto-rate would change the unit's rate */
  wouldChange: boolean;
}

/**
 * Response from auto-rate setting update
 */
export interface AutoRateSettingUpdateResponse {
  success: boolean;
  entityType: AutoRateEntityType;
  entityId: string;
  autoRateByLocation: boolean | null;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * US State codes for rate filtering
 */
export const US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
] as const;

export type USStateCode = typeof US_STATE_CODES[number];

/**
 * NEM version options
 */
export const NEM_VERSIONS = ['NEM 1.0', 'NEM 2.0', 'NEM 3.0'] as const;
export type NEMVersion = typeof NEM_VERSIONS[number];

/**
 * Default system rate values (used as fallback if database unavailable)
 */
export const DEFAULT_SYSTEM_RATES = {
  gridSellRate: 0.08,
  gridImportRate: 0.18,
  dieselCostPerGallon: 4.50,
} as const;

/**
 * Rate source display labels
 */
export const RATE_SOURCE_LABELS: Record<RateSource, string> = {
  unit_override: 'Unit Override',
  location: 'Auto (Location)',
  unit: 'Unit',
  site: 'Site Default',
  group: 'Group Default',
  company: 'Company Default',
  industry: 'Industry Default',
  system: 'System Default',
  fallback: 'Fallback',
};

/**
 * Rate source badge colors (matching site theme)
 */
export const RATE_SOURCE_COLORS: Record<RateSource, string> = {
  unit_override: '#8FB560',  // Hollywood green - explicit override
  location: '#06b6d4',       // Cyan - auto-location
  unit: '#22c55e',           // Green - unit level
  site: '#3b82f6',           // Blue - local
  group: '#8b5cf6',          // Purple - group level
  company: '#f59e0b',        // Amber - company wide
  industry: '#ec4899',       // Pink - industry standard
  system: '#6b7280',         // Gray - system default
  fallback: '#ef4444',       // Red - fallback (warning)
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format rate as currency string
 */
export function formatRate(rate: number, digits: number = 2): string {
  return `$${rate.toFixed(digits)}`;
}

/**
 * Format rate for display with unit
 */
export function formatRatePerKwh(rate: number): string {
  return `$${rate.toFixed(2)}/kWh`;
}

/**
 * Calculate grid sell revenue
 */
export function calculateGridSellRevenue(gridExportKwh: number, rate: number): number {
  return Math.round(gridExportKwh * rate * 100) / 100;
}

/**
 * Calculate grid import cost
 */
export function calculateGridImportCost(gridImportKwh: number, rate: number): number {
  return Math.round(gridImportKwh * rate * 100) / 100;
}

/**
 * Calculate net grid revenue (sell - import)
 */
export function calculateNetGridRevenue(
  gridExportKwh: number,
  gridImportKwh: number,
  sellRate: number,
  importRate: number
): number {
  const revenue = calculateGridSellRevenue(gridExportKwh, sellRate);
  const cost = calculateGridImportCost(gridImportKwh, importRate);
  return Math.round((revenue - cost) * 100) / 100;
}

/**
 * Calculate cost savings from solar vs grid
 */
export function calculateSolarSavings(solarKwh: number, importRate: number): number {
  return Math.round(solarKwh * importRate * 100) / 100;
}

/**
 * Transform database snake_case to camelCase
 */
export function transformRateFromDb(dbRate: Record<string, unknown>): EnergyRate {
  return {
    id: dbRate.id as string,
    name: dbRate.name as string,
    description: dbRate.description as string | undefined,
    gridSellRate: Number(dbRate.grid_sell_rate),
    gridImportRate: Number(dbRate.grid_import_rate),
    dieselCostPerGallon: Number(dbRate.diesel_cost_per_gallon),
    utilityProvider: dbRate.utility_provider as string | undefined,
    utilityProviderId: dbRate.utility_provider_id as string | undefined,
    nemVersion: dbRate.nem_version as string | undefined,
    countryCode: dbRate.country_code as string | undefined,
    stateProvince: dbRate.state_province as string | undefined,
    isSystemDefault: Boolean(dbRate.is_system_default),
    isLocked: Boolean(dbRate.is_locked),
    createdAt: dbRate.created_at as string,
    updatedAt: dbRate.updated_at as string,
  };
}

/**
 * Transform camelCase to database snake_case
 */
export function transformRateToDb(rate: RateFormData): Record<string, unknown> {
  return {
    name: rate.name,
    description: rate.description,
    grid_sell_rate: rate.gridSellRate,
    grid_import_rate: rate.gridImportRate,
    diesel_cost_per_gallon: rate.dieselCostPerGallon,
    utility_provider: rate.utilityProvider,
    utility_provider_id: rate.utilityProviderId || null,
    nem_version: rate.nemVersion,
    country_code: rate.countryCode || 'US',
    state_province: rate.stateProvince,
    is_locked: rate.isLocked,
  };
}

/**
 * Transform rate assignment from database snake_case to camelCase
 */
export function transformRateAssignmentFromDb(
  dbAssignment: Record<string, unknown>
): RateAssignmentRecord {
  return {
    id: dbAssignment.id as string,
    rateId: dbAssignment.rate_id as string,
    entityType: dbAssignment.entity_type as 'company' | 'group' | 'unit',
    entityId: dbAssignment.entity_id as string,
    effectiveFrom: dbAssignment.effective_from as string,
    effectiveTo: (dbAssignment.effective_to as string) || null,
    isOverride: Boolean(dbAssignment.is_override),
    priority: Number(dbAssignment.priority || 0),
    createdAt: dbAssignment.created_at as string,
    createdBy: (dbAssignment.created_by as string) || null,
    updatedAt: dbAssignment.updated_at as string,
  };
}

/**
 * Transform rate assignment form data to database snake_case
 */
export function transformRateAssignmentToDb(
  assignment: RateAssignmentFormData
): Record<string, unknown> {
  return {
    rate_id: assignment.rateId,
    entity_type: assignment.entityType,
    entity_id: assignment.entityId,
    effective_from: assignment.effectiveFrom,
    effective_to: assignment.effectiveTo || null,
    is_override: assignment.isOverride ?? false,
    priority: assignment.priority ?? 0,
  };
}

/**
 * Transform effective rate response from database (get_effective_rate_for_date returns JSONB)
 */
export function transformEffectiveRateResponse(
  dbResponse: Record<string, unknown>
): EffectiveRateResponse {
  return {
    rateId: (dbResponse.rateId as string) || null,
    rateName: dbResponse.rateName as string,
    description: dbResponse.description as string | undefined,
    gridSellRate: Number(dbResponse.gridSellRate || 0),
    gridImportRate: Number(dbResponse.gridImportRate || 0),
    dieselCostPerGallon: Number(dbResponse.dieselCostPerGallon || 0),
    utilityProvider: dbResponse.utilityProvider as string | undefined,
    nemVersion: dbResponse.nemVersion as string | undefined,
    stateProvince: dbResponse.stateProvince as string | undefined,
    rateSource: dbResponse.rateSource as RateSource,
    sourceName: dbResponse.sourceName as string,
    effectiveFrom: (dbResponse.effectiveFrom as string) || null,
    effectiveTo: (dbResponse.effectiveTo as string) || null,
  };
}

/**
 * Format effective date range for display
 */
export function formatEffectivePeriod(
  effectiveFrom: string | null | undefined,
  effectiveTo: string | null | undefined
): string {
  if (!effectiveFrom) {
    return 'Always';
  }

  const fromDate = new Date(effectiveFrom).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  if (!effectiveTo) {
    return `From ${fromDate} (ongoing)`;
  }

  const toDate = new Date(effectiveTo).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${fromDate} - ${toDate}`;
}
